import { ExternalLink } from 'lucide-react';
import Image from 'next/image';
import NextLink from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useCurrency } from 'src/modules/currencies/hooks/useCurrency';
import { TokenDisplayInfo } from 'src/modules/currencies/types';
import { resolveTokenMetadata } from 'src/modules/triggers/infra/tokenService';

import { Badge, Skeleton, Text, Tooltip } from '@/design-system/components/ui';
import { getAddressScannerUrl, maskAddressValue } from '@/utils/format';

interface Props {
  value: string;
  chainId?: number;
  isDuneColumn?: boolean;
  enableExternalResolution?: boolean;
}

export function CurrencyDisplay({ value, chainId, isDuneColumn = false, enableExternalResolution = true }: Props) {
  const isAddress = useMemo(() => /^0x[a-fA-F0-9]{40}$/.test(value), [value]);

  const { data: currency } = useCurrency(
    isAddress ? value : undefined,
    chainId ? String(chainId) : undefined,
    isAddress && !!chainId,
  );

  const [enhancedTokenInfo, setEnhancedTokenInfo] = useState<TokenDisplayInfo | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    const resolveToken = async () => {
      if (!isAddress || !chainId || !enableExternalResolution) return;

      // API already resolved this currency; no external resolution needed.
      if (currency) return;

      setIsResolving(true);
      try {
        const result = await resolveTokenMetadata(value, chainId);
        if (result.metadata) {
          setEnhancedTokenInfo({
            symbol: result.metadata.symbol,
            name: result.metadata.name,
            logoURI: result.metadata.logoURI,
            decimals: result.metadata.decimals,
            isVerified: result.metadata.isVerified,
            source: result.source,
          });
        }
      } catch (error) {
        console.warn('Enhanced token resolution failed:', error);
      } finally {
        setIsResolving(false);
      }
    };

    resolveToken();
  }, [value, chainId, isAddress, enableExternalResolution, currency]);

  if (!isAddress) {
    return (
      <div className="tw:flex tw:flex-row tw:gap-1">
        <Badge variant="outline" size="sm">
          {value}
        </Badge>
        <Text as="span" variant="body-sm">
          {isDuneColumn ? 'column' : 'parameter'}
        </Text>
      </div>
    );
  }

  const symbol = currency?.name ?? value;
  const logoURI = currency?.logoUri ?? null;
  const scannerUrl = chainId ? getAddressScannerUrl('chainId', chainId, value) : null;

  if (isResolving && isAddress && chainId) {
    return <Skeleton variant="rectangular" width={120} height={24} className="tw:rounded-lg" />;
  }

  const displaySymbol = enhancedTokenInfo?.symbol ?? symbol;
  const displayLogoURI = enhancedTokenInfo?.logoURI ?? logoURI;
  const isVerified = enhancedTokenInfo?.isVerified ?? symbol !== value;

  if (displaySymbol !== value && (displayLogoURI || enhancedTokenInfo)) {
    const content = (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {displayLogoURI && <Image src={displayLogoURI} alt={displaySymbol} width={16} height={16} />}
        <span>{displaySymbol}</span>
        {enhancedTokenInfo && !isVerified && (
          <Text as="span" variant="caption" color="warning">
            (unverified)
          </Text>
        )}
        {scannerUrl && <ExternalLink size={12} />}
      </div>
    );

    if (scannerUrl) {
      return (
        <NextLink
          href={scannerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="tw:flex tw:gap-0.5 tw:no-underline"
        >
          <Badge size="sm" variant={isVerified ? 'default' : 'warning'}>
            {content}
          </Badge>
        </NextLink>
      );
    }

    return (
      <Badge size="sm" variant={isVerified ? 'default' : 'warning'}>
        {content}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" size="sm">
      <Tooltip content={value}>
        <Text as="span" variant="body-sm">
          {maskAddressValue(value)}
        </Text>
      </Tooltip>
    </Badge>
  );
}
